/**
 * CRM > Pipeline. A kanban board of open opportunities grouped by stage
 * (GET /v1/crm/pipeline), with a list-view toggle (GET /v1/crm/opportunities).
 *
 * Each card shows the account, rep, projected + weighted amounts, expected
 * close, and probability. A card's stage can be moved via a per-card stage
 * dropdown (POST /opportunities/:id/stage). "New opportunity" opens a modal
 * (POST /v1/crm/opportunities).
 */

import { useCallback, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Loader2, Plus, X } from 'lucide-react';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { Pill } from '@/components/Pill';
import { DataGridShell } from '@/components/grid/DataGridShell';
import { SortableHeader, gridTheadClassName, type SortDir } from '@ampersandbrands/ui';
import { useSession } from '@/lib/useSession';
import { formatDateShort } from '@/lib/dateUtils';
import {
  formatCrmMoney,
  formatProbabilityPct,
  hasCrmPipeline,
  oppStatusTone,
  useCreateOpportunity,
  useCrmAccounts,
  useCrmOpportunities,
  useCrmPipeline,
  useCrmReps,
  useCrmStages,
  useMoveStage,
  type CreateOpportunityBody,
  type OpportunityRow,
  type PipelineStage,
} from '@/lib/useCrm';

type ViewMode = 'board' | 'list';

export function CrmPipeline() {
  const { user } = useSession();
  // No more read/write levels: holding crm.pipeline grants both.
  const canRead = hasCrmPipeline(user?.permissions, user?.superAdmin);
  const canWrite = canRead;

  const [searchParams, setSearchParams] = useSearchParams();
  const view: ViewMode = searchParams.get('view') === 'list' ? 'list' : 'board';
  const setView = (v: ViewMode) =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (v === 'board') next.delete('view');
      else next.set('view', v);
      return next;
    }, { replace: true });

  const [addOpen, setAddOpen] = useState(false);

  if (user && !canRead) return <AccessDeniedCard />;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-start justify-between mb-6 gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">CRM</p>
          <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">Pipeline</h1>
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
            Open opportunities by stage. Move a card through the stages as a deal progresses.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <ToolPinToggle toolSlug="opportunities" toolName="Pipeline" />
          <div className="inline-flex rounded border border-amp-line dark:border-amp-dark-line overflow-hidden">
            {(['board', 'list'] as ViewMode[]).map((v, idx) => (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                className={`text-xs px-3 py-1.5 capitalize ${idx > 0 ? 'border-l border-amp-line dark:border-amp-dark-line' : ''} ${
                  view === v
                    ? 'bg-amp-ink text-amp-white dark:bg-amp-dark-ink dark:text-amp-dark-surface'
                    : 'text-amp-ink hover:bg-amp-cream-warm dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm'
                }`}
              >
                {v}
              </button>
            ))}
          </div>
          {canWrite && (
            <button
              type="button"
              onClick={() => setAddOpen(true)}
              className="inline-flex items-center gap-1 text-xs border border-amp-ink bg-amp-ink text-amp-white rounded px-3 py-1.5 hover:opacity-90 dark:border-amp-dark-ink dark:bg-amp-dark-ink dark:text-amp-dark-surface"
            >
              <Plus className="h-3 w-3" /> New opportunity
            </button>
          )}
        </div>
      </div>

      {view === 'board' ? <BoardView canWrite={canWrite} /> : <ListView />}

      {addOpen && <NewOpportunityModal onClose={() => setAddOpen(false)} />}
    </div>
  );
}

// -------- board view ---------------------------------------------------------

function BoardView({ canWrite }: { canWrite: boolean }) {
  const pipelineQ = useCrmPipeline();

  if (pipelineQ.isLoading) {
    return <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">Loading pipeline...</p>;
  }
  if (pipelineQ.error) {
    return <p className="text-sm text-red-700 dark:text-red-300">Could not load: {pipelineQ.error.message}</p>;
  }
  const stages = pipelineQ.data?.stages ?? [];
  return (
    <div className="min-h-0 flex-1 overflow-x-auto">
      <div className="flex gap-3 h-full min-h-0 pb-2">
        {stages.map((stage) => (
          <StageColumn key={stage.id} stage={stage} allStages={stages} canWrite={canWrite} />
        ))}
      </div>
    </div>
  );
}

function StageColumn({
  stage,
  allStages,
  canWrite,
}: {
  stage: PipelineStage;
  allStages: PipelineStage[];
  canWrite: boolean;
}) {
  return (
    <div className="flex w-72 shrink-0 flex-col rounded-lg border border-amp-line bg-amp-white dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <div className="shrink-0 border-b border-amp-line px-3 py-2 dark:border-amp-dark-line">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-amp-ink dark:text-amp-dark-ink">{stage.name}</h2>
          <span className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">{stage.count}</span>
        </div>
        <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
          {formatCrmMoney(stage.weighted_amount)} weighted
        </p>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-2 space-y-2">
        {stage.opportunities.length === 0 ? (
          <p className="px-1 py-2 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">No opportunities.</p>
        ) : (
          stage.opportunities.map((o) => (
            <OpportunityCard key={o.id} opp={o} stages={allStages} canWrite={canWrite} />
          ))
        )}
      </div>
    </div>
  );
}

function OpportunityCard({
  opp,
  stages,
  canWrite,
}: {
  opp: OpportunityRow;
  stages: PipelineStage[];
  canWrite: boolean;
}) {
  const move = useMoveStage();
  return (
    <div className="rounded-md border border-amp-line bg-amp-cream-warm p-2.5 dark:border-amp-dark-line dark:bg-amp-dark-surface-warm">
      <Link
        to={`/crm/opportunities/${opp.id}`}
        className="block text-sm font-medium text-amp-ink hover:underline dark:text-amp-dark-ink"
      >
        {opp.title}
      </Link>
      <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">{opp.account_name ?? '--'}</p>
      <div className="mt-1.5 flex items-center justify-between text-xs text-amp-ink dark:text-amp-dark-ink">
        <span>{formatCrmMoney(opp.projected_amount)}</span>
        <span className="text-amp-ink-muted dark:text-amp-dark-ink-muted">{formatProbabilityPct(opp.probability)}</span>
      </div>
      <div className="mt-1 flex items-center justify-between text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
        <span>{opp.sales_rep_name ?? 'Unassigned'}</span>
        <span>{formatDateShort(opp.expected_close_date)}</span>
      </div>
      <p className="mt-1 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
        Weighted {formatCrmMoney(opp.weighted_amount)}
      </p>
      {canWrite && (
        <div className="mt-2">
          <label className="sr-only" htmlFor={`move-${opp.id}`}>Move stage</label>
          <select
            id={`move-${opp.id}`}
            value={opp.stage_id}
            disabled={move.isPending}
            onChange={(e) => move.mutate({ id: opp.id, stage_id: Number(e.target.value) })}
            className="w-full border border-amp-line rounded px-2 py-1 text-xs bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
          >
            {stages.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
          {move.isError && <p className="mt-1 text-xs text-red-700 dark:text-red-300">{move.error.message}</p>}
        </div>
      )}
    </div>
  );
}

// -------- list view ----------------------------------------------------------

type OppSortColumn = 'title' | 'account' | 'rep' | 'stage' | 'projected' | 'weighted' | 'close' | 'probability';

function sortValue(o: OpportunityRow, col: OppSortColumn): string | number {
  switch (col) {
    case 'title': return o.title.toLowerCase();
    case 'account': return (o.account_name ?? '').toLowerCase();
    case 'rep': return (o.sales_rep_name ?? '').toLowerCase();
    case 'stage': return (o.stage_name ?? '').toLowerCase();
    case 'projected': return Number(o.projected_amount ?? 0);
    case 'weighted': return Number(o.weighted_amount ?? 0);
    case 'close': return o.expected_close_date ?? '';
    case 'probability': return Number(o.probability ?? 0);
  }
}

function ListView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const oppsQ = useCrmOpportunities({ status: 'open' });

  const update = useCallback(
    (mutate: (sp: URLSearchParams) => void) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        mutate(next);
        return next;
      }, { replace: true });
    },
    [setSearchParams],
  );

  const rawSort = searchParams.get('sort');
  const sort = useMemo(() => {
    if (!rawSort) return null;
    const [col, dir] = rawSort.split(':');
    const cols: OppSortColumn[] = ['title', 'account', 'rep', 'stage', 'projected', 'weighted', 'close', 'probability'];
    if (!cols.includes(col as OppSortColumn)) return null;
    if (dir !== 'asc' && dir !== 'desc') return null;
    return { column: col as OppSortColumn, dir: dir as SortDir };
  }, [rawSort]);

  const toggleSort = (col: OppSortColumn) => {
    if (!sort || sort.column !== col) update((sp) => sp.set('sort', `${col}:asc`));
    else if (sort.dir === 'asc') update((sp) => sp.set('sort', `${col}:desc`));
    else update((sp) => sp.delete('sort'));
  };
  const sortedFor = (col: OppSortColumn): SortDir | null => (sort && sort.column === col ? sort.dir : null);

  const rows = sort
    ? [...oppsQ.rows].sort((a, b) => {
        const av = sortValue(a, sort.column);
        const bv = sortValue(b, sort.column);
        const f = sort.dir === 'asc' ? 1 : -1;
        if (av < bv) return -1 * f;
        if (av > bv) return 1 * f;
        return 0;
      })
    : oppsQ.rows;

  return (
    <DataGridShell
      total={oppsQ.totalCount}
      loaded={rows.length}
      countNoun="opportunities"
      onReachEnd={oppsQ.fetchNextPage}
      hasMore={oppsQ.hasNextPage}
      isLoadingMore={oppsQ.isFetchingNextPage}
    >
      <table className="min-w-full text-sm">
        <thead className={gridTheadClassName}>
          <tr>
            <SortableHeader label="Opportunity" sorted={sortedFor('title')} onToggle={() => toggleSort('title')} thClassName="px-3 py-2" />
            <SortableHeader label="Account" sorted={sortedFor('account')} onToggle={() => toggleSort('account')} thClassName="px-3 py-2" />
            <SortableHeader label="Rep" sorted={sortedFor('rep')} onToggle={() => toggleSort('rep')} thClassName="px-3 py-2 w-[140px]" />
            <SortableHeader label="Stage" sorted={sortedFor('stage')} onToggle={() => toggleSort('stage')} thClassName="px-3 py-2 w-[130px]" />
            <SortableHeader label="Projected" sorted={sortedFor('projected')} onToggle={() => toggleSort('projected')} align="right" thClassName="px-3 py-2 w-[110px]" />
            <SortableHeader label="Weighted" sorted={sortedFor('weighted')} onToggle={() => toggleSort('weighted')} align="right" thClassName="px-3 py-2 w-[110px]" />
            <SortableHeader label="Prob" sorted={sortedFor('probability')} onToggle={() => toggleSort('probability')} align="right" thClassName="px-3 py-2 w-[70px]" />
            <SortableHeader label="Close" sorted={sortedFor('close')} onToggle={() => toggleSort('close')} thClassName="px-3 py-2 w-[100px]" />
          </tr>
        </thead>
        <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
          {oppsQ.isLoading ? (
            <tr><td colSpan={8} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">Loading opportunities...</td></tr>
          ) : oppsQ.error ? (
            <tr><td colSpan={8} className="px-4 py-6 text-red-700 dark:text-red-300">Could not load: {oppsQ.error.message}</td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={8} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">No open opportunities.</td></tr>
          ) : (
            rows.map((o) => (
              <tr key={o.id} className="hover:bg-amp-cream-warm dark:hover:bg-amp-dark-surface-warm">
                <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">
                  <Link to={`/crm/opportunities/${o.id}`} className="hover:underline">{o.title}</Link>
                  <div className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted font-mono">{o.opp_no}</div>
                </td>
                <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">
                  {o.account_id ? <Link to={`/crm/accounts/${o.account_id}`} className="hover:underline">{o.account_name ?? '--'}</Link> : (o.account_name ?? '--')}
                </td>
                <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">{o.sales_rep_name ?? '--'}</td>
                <td className="px-3 py-2"><Pill tone={oppStatusTone(o.status)}>{o.stage_name ?? '--'}</Pill></td>
                <td className="px-3 py-2 text-right text-amp-ink dark:text-amp-dark-ink whitespace-nowrap">{formatCrmMoney(o.projected_amount)}</td>
                <td className="px-3 py-2 text-right text-amp-ink dark:text-amp-dark-ink whitespace-nowrap">{formatCrmMoney(o.weighted_amount)}</td>
                <td className="px-3 py-2 text-right text-amp-ink-muted dark:text-amp-dark-ink-muted whitespace-nowrap">{formatProbabilityPct(o.probability)}</td>
                <td className="px-3 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted whitespace-nowrap">{formatDateShort(o.expected_close_date)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </DataGridShell>
  );
}

// -------- new opportunity modal ----------------------------------------------

function NewOpportunityModal({ onClose }: { onClose: () => void }) {
  const { stages } = useCrmStages();
  const { reps } = useCrmReps();
  // Load a page of accounts for the picker. The account list is keyset; the
  // first page (50) covers the common case, and the search box narrows it.
  const [accountQuery, setAccountQuery] = useState('');
  const accountsQ = useCrmAccounts({ q: accountQuery || undefined });
  const create = useCreateOpportunity();

  const openStages = stages.filter((s) => s.is_open);

  const [form, setForm] = useState({
    account_id: '',
    stage_id: '',
    title: '',
    sales_rep_id: '',
    projected_amount: '',
    expected_close_date: '',
    memo: '',
  });
  const [err, setErr] = useState<string | null>(null);
  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const submit = () => {
    setErr(null);
    if (!form.account_id) { setErr('Account is required.'); return; }
    if (!form.stage_id) { setErr('Stage is required.'); return; }
    if (!form.title.trim()) { setErr('Title is required.'); return; }
    const body: CreateOpportunityBody = {
      account_id: Number(form.account_id),
      stage_id: Number(form.stage_id),
      title: form.title.trim(),
      sales_rep_id: form.sales_rep_id ? Number(form.sales_rep_id) : null,
      projected_amount: form.projected_amount ? Number(form.projected_amount) : undefined,
      expected_close_date: form.expected_close_date || null,
      memo: form.memo || null,
    };
    create.mutate(body, { onSuccess: () => onClose(), onError: (e) => setErr(e.message) });
  };

  const inputCls =
    'w-full border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink';
  const labelCls = 'block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1';

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" role="dialog">
      <div className="relative w-full max-w-md rounded-lg border border-amp-line bg-amp-white p-5 shadow-2 dark:border-amp-dark-line dark:bg-amp-dark-surface">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
        <h2 className="text-lg font-semibold text-amp-ink dark:text-amp-dark-ink mb-4">New opportunity</h2>
        <div className="space-y-3">
          <div>
            <label className={labelCls}>Account</label>
            <input
              type="text"
              value={accountQuery}
              onChange={(e) => setAccountQuery(e.target.value)}
              placeholder="Search accounts..."
              className={`${inputCls} mb-1`}
            />
            <select value={form.account_id} onChange={(e) => set('account_id', e.target.value)} className={inputCls}>
              <option value="">Select an account</option>
              {accountsQ.rows.map((a) => (
                <option key={a.id} value={String(a.id)}>{a.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Title</label>
            <input type="text" value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="e.g. Q3 holiday reorder" className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Stage</label>
              <select value={form.stage_id} onChange={(e) => set('stage_id', e.target.value)} className={inputCls}>
                <option value="">Select</option>
                {openStages.map((s) => (
                  <option key={s.id} value={String(s.id)}>{s.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Rep</label>
              <select value={form.sales_rep_id} onChange={(e) => set('sales_rep_id', e.target.value)} className={inputCls}>
                <option value="">Unassigned</option>
                {reps.map((r) => (
                  <option key={r.id} value={String(r.id)}>{r.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Projected amount</label>
              <input type="number" step="0.01" value={form.projected_amount} onChange={(e) => set('projected_amount', e.target.value)} placeholder="0.00" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Expected close</label>
              <input type="date" value={form.expected_close_date} onChange={(e) => set('expected_close_date', e.target.value)} className={inputCls} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Memo</label>
            {/* Stays a plain box. The memo is rendered as plain text on the
                opportunity page, and nothing sanitizes HTML on this field yet.
                Backend tracked in api#563, not approved. */}
            <textarea rows={2} value={form.memo} onChange={(e) => set('memo', e.target.value)} className={inputCls} />
          </div>
        </div>
        {err && <p className="mt-3 text-xs text-red-700 dark:text-red-300">{err}</p>}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="text-xs border border-amp-line rounded px-3 py-1.5 text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm">
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={create.isPending}
            className="text-xs border border-amp-ink bg-amp-ink text-amp-white rounded px-3 py-1.5 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-ink dark:bg-amp-dark-ink dark:text-amp-dark-surface inline-flex items-center gap-1"
          >
            {create.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
            Create
          </button>
        </div>
      </div>
    </div>
  );
}

function AccessDeniedCard() {
  return (
    <div className="max-w-xl rounded-lg border border-amp-line p-5 dark:border-amp-dark-line">
      <h2 className="text-lg font-semibold text-amp-ink dark:text-amp-dark-ink mb-1">No access</h2>
      <p className="text-sm text-amp-ink dark:text-amp-dark-ink">
        CRM is gated by the crm:read capability. Ask IT to grant you access.
      </p>
    </div>
  );
}
